<template>
  <dx-modal :value="show" title="Nova guia" size="md" @input="$emit('close')"
            id="modal-new-guide-picker" class="gd-scope">
    <div class="stenci-group stenci-info gd-picker-help">
      Escolha o paciente e o agendamento com serviços pendentes; a guia é montada com esses
      itens, do convênio escolhido.
    </div>
    <div class="columns">
      <div class="column col-12 form-group">
        <label class="form-label">Paciente</label>
        <dx-autocomplete
          v-model="patient"
          :source="findPatient"
          label="name"
          track-by="id"
          :debounce="600"
          :readonly="!!patient"
          @select="selectPatient"
          placeholder="Nome, CPF ou telefone"
          input-id="new-guide-patient">
          <template v-slot="{ item }">
            <a>
              <fa-icon :icon="['fal', 'user']" class="text-primary"/>
              <span class="ml-1">{{ item.name }}</span>
              <small class="text-gray ml-1" v-if="item.birthDate">
                {{ item.birthDate | date }}
              </small>
            </a>
          </template>
          <template v-slot:action>
            <button type="button" class="btn btn-gray btn-action input-group-btn btn-icon"
                    tabindex="-1" v-if="patient" @click="clearPatient">
              <fa-icon :icon="['fal', 'times']"/>
            </button>
          </template>
        </dx-autocomplete>
      </div>
    </div>

    <template v-if="patient">
      <div class="loading loading-lg" v-if="loading"></div>
      <template v-else>
        <div class="empty" v-if="appointments.length === 0">
          <div class="empty-icon"><fa-icon :icon="['fal', 'info-circle']" size="2x"/></div>
          <p class="empty-title h6">Nenhum agendamento com serviços pendentes</p>
          <p class="empty-subtitle">
            Nos últimos 90 dias não há serviços de convênio sem guia para este paciente.
          </p>
        </div>
        <div class="cr-panel" v-else>
          <div class="cr-panel-head">
            <div class="cr-panel-title">
              <fa-icon :icon="['fal', 'calendar-check']" class="cr-panel-icon"/>
              Agendamentos
              <span class="label label-rounded">{{ appointments.length }}</span>
            </div>
            <div class="cr-panel-subtitle">Últimos 90 dias com serviços de convênio sem guia</div>
          </div>
          <table class="table table-hover">
            <thead>
            <tr>
              <th>Data</th>
              <th>Profissional</th>
              <th>Convênio</th>
              <th class="text-center">Pendentes</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="row in appointments" :key="row.id"
                :class="{active: selected && selected.id === row.id}">
              <td class="text-nowrap">{{ row.startDate | date('datetime') }}</td>
              <td>{{ row.professional.name }}</td>
              <td>{{ row.insuranceName }}</td>
              <td class="text-center">{{ row.pending.length }}</td>
              <td class="text-right">
                <button class="btn btn-sm btn-secondary" @click="selectAppointment(row)">
                  Escolher
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <div class="columns gd-picker-options" v-if="selected">
          <div class="column col-7 col-sm-12 form-group"
               :class="{'has-error': $v.payerId.$error}">
            <label class="form-label">Convênio da guia</label>
            <select class="form-select" v-model="payerId" @change="payerChanged">
              <option value="">[Selecione]</option>
              <option v-for="payer in selected.payers" :value="payer.planId" :key="payer.planId">
                {{ payer.name }} · {{ payer.count }} {{ payer.count === 1 ? 'item' : 'itens' }}
              </option>
            </select>
            <div class="form-input-hint" v-if="$v.payerId.$error">Campo obrigatório</div>
          </div>
          <div class="column col-5 col-sm-12 form-group"
               :class="{'has-error': $v.type.$error}">
            <label class="form-label">Tipo de guia</label>
            <select class="form-select" v-model="type" :disabled="loadingPlan">
              <option value="">[Selecione]</option>
              <option v-for="option in typeOptions" :value="option.value" :key="option.value">
                {{ option.label }}
              </option>
            </select>
            <div class="form-input-hint" v-if="$v.type.$error">Campo obrigatório</div>
          </div>
        </div>
      </template>
    </template>

    <template slot="footer">
      <button class="btn btn-primary btn-icon btn-icon-left mr-1"
              :class="{loading: loadingPlan}" :disabled="!selected || loadingPlan" @click="pick">
        <fa-icon :icon="['fal', 'file-medical-alt']"/>Montar guia
      </button>
      <button class="btn" @click="$emit('close')">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import moment from 'moment';
import { required } from 'vuelidate/lib/validators';
import { customName } from '@/helpers/insurance';
import { types } from '@/data/billing-guide-types';
import {
  APPOINTMENT_GENERATION,
  billingGeneration,
  idOf,
  isLegacyExpense,
  isParticularPlan,
  loadPlan,
} from '../service';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      patient: null,
      loading: false,
      loadingPlan: false,
      appointments: [],
      selected: null,
      payerId: '',
      type: '',
      tissEnabled: false,
      consultationInSadt: false,
    };
  },
  validations: {
    payerId: { required },
    type: { required },
  },
  computed: {
    typeOptions() {
      if (!this.tissEnabled) return [{ value: 'simple', label: types.simple }];
      const values = this.consultationInSadt || !this.hasPendingConsultation
        ? ['sadt']
        : ['consultation', 'sadt'];
      return values.map(value => ({ value, label: types[value] }));
    },
    hasPendingConsultation() {
      if (!this.selected || !this.currentPayer) return false;
      return this.selected.pending.some(expense => (
        this.currentPayer.expenseIds.includes(String(expense.id))
        && expense.expense.type === 'consultation'
      ));
    },
    currentPayer() {
      return this.selected
        ? this.selected.payers.find(payer => payer.planId === this.payerId) || null
        : null;
    },
  },
  methods: {
    findPatient(search) {
      return this.$http.get('/patients', { params: { search, limit: 10 } })
        .then(({ data }) => data.items)
        .catch(() => []);
    },
    selectPatient(patient) {
      if (!patient) return;
      this.patient = patient;
      this.loadAppointments();
    },
    clearPatient() {
      this.patient = null;
      this.appointments = [];
      this.selected = null;
      this.payerId = '';
      this.type = '';
      this.$v.$reset();
    },
    insuranceOf(expense, appointment) {
      const own = expense.insurance && expense.insurance.plan && idOf(expense.insurance.plan);
      return own ? expense.insurance : appointment.insurance;
    },
    pendingOf(appointment) {
      if (billingGeneration(appointment.expenses) === APPOINTMENT_GENERATION.LEGACY) return [];
      return (appointment.expenses || []).filter((expense) => {
        const billing = expense.billing || {};
        if (idOf(billing.accountId) || isLegacyExpense(expense)) return false;
        const insurance = this.insuranceOf(expense, appointment);
        return insurance && !isParticularPlan(insurance);
      });
    },
    payersOf(appointment, pending) {
      const payers = [];
      pending.forEach((expense) => {
        const insurance = this.insuranceOf(expense, appointment);
        const planId = idOf(insurance.plan);
        const found = payers.find(payer => payer.planId === planId);
        if (found) {
          found.count += 1;
          found.expenseIds.push(String(expense.id));
        } else {
          payers.push({
            planId,
            insuranceId: idOf(insurance),
            name: customName({ name: insurance.name, plan: { name: insurance.plan.name || '' } }),
            count: 1,
            expenseIds: [String(expense.id)],
          });
        }
      });
      return payers;
    },
    loadAppointments() {
      this.loading = true;
      this.selected = null;
      const params = {
        patientId: this.patient.id,
        startDate: moment().subtract(90, 'days').startOf('day').format('YYYY-MM-DDTHH:mm'),
        endDate: moment().endOf('day').format('YYYY-MM-DDTHH:mm'),
        limit: 100,
      };
      this.$http.get('/appointments', { params })
        .then(({ data }) => {
          this.appointments = (data.items || [])
            .map((appointment) => {
              const pending = this.pendingOf(appointment);
              const payers = this.payersOf(appointment, pending);
              return {
                id: appointment.id,
                startDate: appointment.startDate,
                professional: appointment.professional || {},
                insuranceName: payers.map(payer => payer.name).join(', '),
                pending,
                payers,
                source: appointment,
              };
            })
            .filter(row => row.pending.length > 0)
            .sort((a, b) => (a.startDate > b.startDate ? -1 : 1));
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    selectAppointment(row) {
      this.selected = row;
      this.payerId = row.payers.length === 1 ? row.payers[0].planId : '';
      this.type = '';
      this.$v.$reset();
      if (this.payerId) this.payerChanged();
    },
    async payerChanged() {
      const payer = this.currentPayer;
      this.type = '';
      if (!payer) return;
      this.loadingPlan = true;
      try {
        const plan = await loadPlan(payer.insuranceId, payer.planId);
        const tiss = plan && plan.settings && plan.settings.tiss ? plan.settings.tiss : {};
        this.tissEnabled = !!tiss.enabled;
        this.consultationInSadt = !!(tiss.consultation && tiss.consultation.sadt);
        if (!this.tissEnabled) {
          this.type = 'simple';
        } else if (this.consultationInSadt) {
          this.type = 'sadt';
        } else {
          const expenses = this.selected.pending
            .filter(expense => payer.expenseIds.includes(String(expense.id)));
          this.type = expenses.every(expense => expense.expense.type === 'consultation')
            ? 'consultation'
            : 'sadt';
        }
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.loadingPlan = false;
      }
    },
    pick() {
      this.$v.$touch();
      if (this.$v.$error || !this.selected) return;
      const payer = this.currentPayer;
      this.$emit('pick', {
        appointment: this.selected.source,
        insurance: { id: payer.insuranceId, planId: payer.planId, name: payer.name },
        type: this.type,
        preselectedExpenseIds: this.expenseIdsFor(payer),
      });
    },
    expenseIdsFor(payer) {
      if (this.type === 'simple' || (this.type === 'sadt' && this.consultationInSadt)) {
        return payer.expenseIds;
      }
      const wantsConsultation = this.type === 'consultation';
      const ids = this.selected.pending
        .filter(expense => payer.expenseIds.includes(String(expense.id))
          && (expense.expense.type === 'consultation') === wantsConsultation)
        .map(expense => String(expense.id));
      return ids.length > 0 ? ids : payer.expenseIds;
    },
  },
};
</script>

<style lang="scss">
@import "../guide.scss";

#modal-new-guide-picker {
  .gd-picker-help {
    font-size: $font-size-sm;
    margin-top: 0;
  }
  .gd-picker-options {
    margin-top: $layout-spacing-lg;
  }
}
</style>
